import { ApiError, getUnauthorizedHandler } from "./client";
import type { FileDTO, FileUploadResponse } from "./types";

export function downloadUrl(id: string): string {
  return `/api/files/${id}/download`;
}

export function previewUrl(id: string): string {
  return `/api/files/${id}/preview`;
}

export interface UploadArgs {
  folderId: string | null;
  file: File;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

/**
 * Upload a single file via XMLHttpRequest (fetch lacks an upload-progress
 * event). The cookie rides along via withCredentials. Root folder = omit the
 * `folderId` field (server normalizes). Maps the {error} envelope to ApiError;
 * a 401 fires the registered unauthorized handler.
 */
export function uploadFile({ folderId, file, onProgress, signal }: UploadArgs): Promise<FileDTO> {
  return new Promise<FileDTO>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/files");
    xhr.withCredentials = true;

    const form = new FormData();
    if (folderId !== null) {
      form.append("folderId", folderId);
    }
    form.append("file", file);

    const onAbort = () => xhr.abort();
    if (signal) {
      signal.addEventListener("abort", onAbort);
    }
    const cleanup = () => {
      if (signal) {
        signal.removeEventListener("abort", onAbort);
      }
    };

    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (e: ProgressEvent) => {
        if (e.lengthComputable && e.total > 0) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      cleanup();
      const status = xhr.status;
      const raw = xhr.responseText;

      if (status === 201) {
        try {
          const body = JSON.parse(raw) as FileUploadResponse;
          resolve(body.file);
        } catch {
          reject(new ApiError(status, "INTERNAL_ERROR", "Something went wrong. Please try again."));
        }
        return;
      }

      // Error path: parse the envelope defensively.
      let code = "INTERNAL_ERROR";
      let message = "Something went wrong. Please try again.";
      let details: unknown;
      try {
        const body = JSON.parse(raw) as { error?: { code: string; message: string; details?: unknown } };
        if (body.error) {
          code = body.error.code;
          message = body.error.message;
          details = body.error.details;
        }
      } catch {
        // keep defaults
      }

      if (status === 401) {
        getUnauthorizedHandler()?.();
      }

      reject(new ApiError(status, code, message, details));
    };

    xhr.onerror = () => {
      cleanup();
      reject(new ApiError(0, "NETWORK_ERROR", "Something went wrong. Please try again."));
    };

    xhr.onabort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };

    xhr.send(form);
  });
}
